import type { HelpArticle, HelpModule } from './helpContent'

/** Every string an article shows, flattened so one search covers all of it. */
function articleText(article: HelpArticle): string {
  const parts: string[] = [article.title]
  if (article.whatItIs) parts.push(article.whatItIs)
  if (article.steps) parts.push(...article.steps)
  if (article.callout) parts.push(article.callout.text)
  if (article.table) {
    parts.push(...article.table.columns)
    for (const group of article.table.groups) {
      if (group.title) parts.push(group.title)
      for (const row of group.rows) parts.push(...row)
    }
  }
  return parts.join('\n').toLowerCase()
}

export function articleMatches(article: HelpArticle, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return articleText(article).includes(q)
}

export interface HelpFilter {
  query: string
  /** A module id to restrict to, or null for all modules. */
  moduleId: string | null
}

/**
 * The modules to show for a search box value and an optional module pill.
 * A module whose own title matches keeps every article; otherwise only the
 * matching articles survive, and a module left with none is dropped.
 */
export function filterHelpModules(modules: HelpModule[], { query, moduleId }: HelpFilter): HelpModule[] {
  const q = query.trim().toLowerCase()
  const scoped = moduleId ? modules.filter((m) => m.id === moduleId) : modules
  if (!q) return scoped
  const out: HelpModule[] = []
  for (const module of scoped) {
    if (module.title.toLowerCase().includes(q)) {
      out.push(module)
      continue
    }
    const articles = module.articles.filter((a) => articleMatches(a, q))
    if (articles.length > 0) out.push({ ...module, articles })
  }
  return out
}
